import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import CollapsibleSection from '../components/CollapsibleSection';
import { assessmentsService } from '../api/assessmentsService';
import {
  markCandidateGreen,
  markCandidateRed,
  markCandidateYellow,
  putCandidateOnGrowthPlan,
} from '../api/onboardingService';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../utils/imageUrl';

const fieldValue = (value: any) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value;
};

const Section: React.FC<{ title: string; description?: string; children: React.ReactNode }> = ({
  title,
  description,
  children,
}) => (
  <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 space-y-4">
    <div>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {description ? <p className="text-sm text-gray-600 mt-1">{description}</p> : null}
    </div>
    {children}
  </div>
);

const InfoGrid: React.FC<{ items: Array<{ label: string; value: any }> }> = ({ items }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    {items.map((item) => (
      <div key={item.label} className="rounded-lg border border-gray-200 px-3 py-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{item.label}</div>
        <div className="text-sm text-gray-900 mt-1">{fieldValue(item.value)}</div>
      </div>
    ))}
  </div>
);

const SurveyBlock: React.FC<{ title: string; survey?: Record<string, any> | null }> = ({ title, survey }) => {
  if (!survey) {
    return (
      <div className="rounded-lg border border-gray-200 px-4 py-3 text-sm text-gray-600">
        {title} not submitted.
      </div>
    );
  }

  const entries = Object.entries(survey)
    .filter(([key]) => !['id', 'userId', 'portalCandidateId', 'surveyFor', 'createdAt', 'updatedAt', 'submittedAt'].includes(key))
    .filter(([, value]) => value !== null && value !== undefined && value !== '');

  return (
    <div className="rounded-lg border border-gray-200 px-4 py-4 space-y-3">
      <div className="text-sm font-semibold text-gray-900">{title}</div>
      {entries.length === 0 ? (
        <div className="text-sm text-gray-600">No responses recorded.</div>
      ) : (
        <div className="space-y-3">
          {entries.map(([key, value]) => (
            <div key={key}>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{key}</div>
              <div className="text-sm text-gray-900 whitespace-pre-wrap mt-1">{String(value)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ status: boolean | string | null | undefined; trueLabel?: string; falseLabel?: string }> = ({
  status,
  trueLabel = 'Completed',
  falseLabel = 'Not Completed',
}) => {
  const isComplete = status === true || status === 'submitted' || status === 'complete';
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
        isComplete ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {isComplete ? trueLabel : falseLabel}
    </span>
  );
};

/* ---------- helpers for Ministry Transitions display ---------- */
const conclusionLabels: Record<string, string> = {
  called_new_position: 'Called to a new ministry position',
  fulfilled_purpose: 'Felt purpose was fulfilled in that role',
  mutual_decision: 'Mutual decision to transition',
  church_closed: 'Church closed or underwent major restructuring',
  personal_family: 'Personal or family reasons',
  conflict: 'Conflict or disagreement',
  asked_to_leave: 'Asked to leave',
  other: 'Other',
};

const PortalCandidateProfile: React.FC = () => {
  const { id, candidateId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const assessmentId = useMemo(() => parseInt(id || '0', 10), [id]);
  const portalCandidateId = useMemo(() => parseInt(candidateId || '0', 10), [candidateId]);

  const hasRole = (roleName: string) =>
    user?.roles?.some((role) => (typeof role === 'string' ? role === roleName : role.name === roleName)) || false;
  const canPromoteToPlanter = hasRole('hub_leader') || hasRole('staff') || hasRole('admin');

  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [promoteError, setPromoteError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['assessments', assessmentId, 'portal-candidates', portalCandidateId, 'profile'],
    queryFn: () => assessmentsService.getPortalCandidateProfile(assessmentId, portalCandidateId),
    enabled: Number.isFinite(assessmentId) && assessmentId > 0 && Number.isFinite(portalCandidateId) && portalCandidateId > 0,
  });

  const candidate = data?.data;
  const migratedPlanterId = candidate?.migratedToChurchPlanterId;

  const markRedMutation = useMutation({
    mutationFn: () => markCandidateRed(portalCandidateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments', assessmentId, 'portal-candidates', portalCandidateId, 'profile'] });
    },
  });

  const markYellowMutation = useMutation({
    mutationFn: () => markCandidateYellow(portalCandidateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments', assessmentId, 'portal-candidates', portalCandidateId, 'profile'] });
    },
  });

  const putGrowthMutation = useMutation({
    mutationFn: () => putCandidateOnGrowthPlan(portalCandidateId),
    onSuccess: (res) => {
      const payload = res?.data as { id?: number } | undefined;
      const pid = payload?.id;
      queryClient.invalidateQueries({ queryKey: ['assessments', assessmentId, 'portal-candidates', portalCandidateId, 'profile'] });
      queryClient.invalidateQueries({ queryKey: ['planters'] });
      if (pid) navigate(`/planters/${pid}`);
    },
    onError: (err: any) => {
      setPromoteError(err?.response?.data?.message || 'Could not put on growth plan.');
    },
  });

  const promoteMutation = useMutation({
    mutationFn: () => markCandidateGreen(portalCandidateId),
    onMutate: () => setPromoteError(null),
    onSuccess: (res) => {
      const payload = res?.data as { id?: number; planter?: { id?: number } } | undefined;
      const planterId = payload?.id ?? payload?.planter?.id;
      queryClient.invalidateQueries({ queryKey: ['assessments', assessmentId, 'portal-candidates', portalCandidateId, 'profile'] });
      queryClient.invalidateQueries({ queryKey: ['assessments', assessmentId] });
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      queryClient.invalidateQueries({ queryKey: ['planters'] });
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidates'] });
      setShowPromoteModal(false);
      setPromoteError(null);
      if (planterId) {
        navigate(`/planters/${planterId}`);
      }
    },
    onError: (err: any) => {
      setPromoteError(err?.response?.data?.message || 'Could not promote to planter. Please try again.');
    },
  });

  const spouseName =
    candidate?.spouseName ||
    [candidate?.wifeFirstName, candidate?.wifeLastName].filter(Boolean).join(' ') ||
    '';
  const displayName = spouseName ? `${candidate?.name || ''} & ${spouseName}`.trim() : candidate?.name || 'Candidate';
  const headshotUrl = getImageUrl(candidate?.headshotImagePath, { width: 200, quality: 70, format: 'webp' });
  const familyPhotoUrl = getImageUrl(candidate?.familyPhotoImagePath, { width: 1400, quality: 70, format: 'webp' });

  const candidateInfo = [
    { label: 'Candidate name', value: candidate?.name },
    { label: 'Candidate email', value: candidate?.email || candidate?.user?.email },
    { label: 'Candidate phone', value: candidate?.phone },
    { label: 'Date of birth', value: candidate?.dateOfBirth },
    { label: 'Marital status', value: candidate?.maritalStatus },
    { label: 'Children', value: candidate?.childrenNamesAges },
  ];

  const spouseInfo = [
    { label: 'Spouse name', value: spouseName },
    { label: 'Spouse email', value: candidate?.wifeEmail },
    { label: 'Spouse phone', value: candidate?.wifePhone },
  ];

  const ministryInfo = [
    { label: 'Target location (MS)', value: candidate?.targetLocationMississippi },
    { label: 'Southern Baptist', value: candidate?.willBeSouthernBaptist },
    { label: 'Ministry leadership', value: candidate?.ministryLeadershipPositions },
    { label: 'Ministry influences', value: candidate?.ministryInfluences },
    { label: 'Theological training', value: candidate?.theologicalTraining },
    { label: 'Sermon link', value: candidate?.sermonReferenceUrl },
    { label: 'Will email sermon', value: candidate?.willEmailSermon },
  ];

  const onboardingStatus = [
    { label: 'Portal status', value: candidate?.portalStatus },
    { label: 'Onboarding step', value: candidate?.onboardingStep },
    { label: 'Onboarding complete', value: candidate?.onboardingCompleted },
    { label: 'Profile complete', value: candidate?.profileCompleted },
    { label: 'Why Church Planting', value: candidate?.whyChurchPlantingCompleted },
    { label: 'Theology complete', value: candidate?.theologyCompleted },
    { label: 'References complete', value: candidate?.referencesCompleted },
    { label: 'Sending church complete', value: candidate?.sendingChurchCompleted },
    { label: 'Prepare/Enrich status', value: candidate?.prepareEnrichStatus },
    { label: 'Spouse Interview', value: candidate?.spouseInterviewSubmitted },
    { label: 'Background check', value: candidate?.backgroundCheckStatus },
    { label: 'Spiritual health complete', value: candidate?.spiritualHealthCompleted },
    { label: 'Ministry Transitions', value: candidate?.ministryTransitionsCompleted },
  ];

  // Why Church Planting data
  const wcpData = candidate?.whyChurchPlantingData as Record<string, any> | null | undefined;

  // Ministry Transitions data
  const mtData = candidate?.ministryTransitionsData as Record<string, any> | null | undefined;
  const mtPositions: any[] = mtData?.positions || [];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Hero header */}
        <div className="bg-white shadow-sm rounded-xl overflow-hidden">
          <div className="relative h-40 sm:h-56">
            {familyPhotoUrl ? (
              <img src={familyPhotoUrl} alt="Family" className="absolute inset-0 h-full w-full object-cover" />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-primary-700" />
            )}
            <div className="absolute inset-0 bg-black/30" />
            <div className="absolute bottom-4 left-4 flex items-center gap-4">
              <Avatar name={displayName} imageUrl={headshotUrl} size="xl" />
              <div>
                <h1 className="text-2xl font-bold text-white">{displayName}</h1>
                <div className="text-sm text-white/80">Portal candidate profile</div>
              </div>
            </div>
            <div className="absolute top-4 right-4 flex flex-wrap items-center justify-end gap-2">
              <Link
                to={`/assessments/${assessmentId}`}
                className="rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-white"
              >
                Back to assessment
              </Link>
              {canPromoteToPlanter && !isLoading && candidate && migratedPlanterId && (
                <Link
                  to={`/planters/${migratedPlanterId}`}
                  className="rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-white"
                >
                  View church planter profile
                </Link>
              )}
              {canPromoteToPlanter && !isLoading && candidate && !migratedPlanterId && (
                <button
                  type="button"
                  onClick={() => {
                    setPromoteError(null);
                    setShowPromoteModal(true);
                  }}
                  className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                >
                  Promote to planter
                </button>
              )}
            </div>
          </div>
          <div className="px-4 py-4 sm:px-6">
            {isLoading && <div className="text-sm text-gray-600">Loading candidate profile...</div>}
            {isError && <div className="text-sm text-red-600">Failed to load candidate profile.</div>}
            {!isLoading && !isError && candidate && (
              <div className="flex flex-col gap-3 text-sm text-gray-700">
                {canPromoteToPlanter && !migratedPlanterId && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">
                      After assessment: record red / yellow / green, put on growth plan, or promote directly to the planting path. Actions
                      archive the portal candidate when a church planter profile is created.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={markRedMutation.isPending}
                        onClick={() => {
                          if (window.confirm('Mark as red (terminal — no church planter profile)?')) markRedMutation.mutate();
                        }}
                        className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-800 hover:bg-red-50 disabled:opacity-50"
                      >
                        Mark red
                      </button>
                      <button
                        type="button"
                        disabled={markYellowMutation.isPending}
                        onClick={() => markYellowMutation.mutate()}
                        className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                      >
                        Mark yellow
                      </button>
                      <button
                        type="button"
                        disabled={putGrowthMutation.isPending}
                        onClick={() => {
                          setPromoteError(null);
                          putGrowthMutation.mutate();
                        }}
                        className="rounded-md border border-amber-600 bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                      >
                        Put on growth plan
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-4">
                <div>
                  Portal progress: <span className="font-semibold text-gray-900">{candidate.portalProgressPercentage ?? 0}%</span>
                </div>
                <div>
                  Team progress: <span className="font-semibold text-gray-900">{candidate.teamProgressPercentage ?? 0}%</span>
                </div>
                <div>
                  Prepare/Enrich: <span className="font-semibold text-gray-900">{candidate.prepareEnrichStatus || '—'}</span>
                </div>
                <div>
                  Background check: <span className="font-semibold text-gray-900">{candidate.backgroundCheckStatus || '—'}</span>
                </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {showPromoteModal && candidate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" role="dialog" aria-modal="true" aria-labelledby="promote-modal-title">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
              <h2 id="promote-modal-title" className="text-lg font-semibold text-gray-900">
                Promote to church planter?
              </h2>
              <p className="text-sm text-gray-600">
                This will create a church planter profile for <span className="font-medium text-gray-900">{displayName}</span>, link their
                assessment history, archive their portal candidate record, and update their account role. This action is not reversible from
                the hub.
              </p>
              {promoteError && <p className="text-sm text-red-600">{promoteError}</p>}
              <div className="flex flex-wrap gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPromoteModal(false);
                    setPromoteError(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={promoteMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => promoteMutation.mutate()}
                  disabled={promoteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  {promoteMutation.isPending ? 'Promoting…' : 'Confirm promote'}
                </button>
              </div>
            </div>
          </div>
        )}

        {!isLoading && !isError && candidate && (
          <>
            {/* ─── Short, always-open sections ─── */}
            <Section title="Candidate Info">
              <InfoGrid items={candidateInfo} />
            </Section>

            <Section title="Spouse Info">
              <InfoGrid items={spouseInfo} />
            </Section>

            <Section title="Ministry + Training">
              <InfoGrid items={ministryInfo} />
            </Section>

            <Section title="Onboarding Status">
              <InfoGrid items={onboardingStatus} />
            </Section>

            {/* ─── Collapsible sections (closed by default) ─── */}

            {/* Why Church Planting */}
            <CollapsibleSection
              title="Why Church Planting"
              badge={<StatusBadge status={candidate.whyChurchPlantingCompleted} />}
            >
              {wcpData && (wcpData.whyPlanting || wcpData.visionForChurch || wcpData.howDecisionMade || wcpData.additionalThoughts) ? (
                <dl className="space-y-4">
                  {wcpData.whyPlanting && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Why do you want to plant a church?</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{wcpData.whyPlanting}</dd>
                    </div>
                  )}
                  {wcpData.visionForChurch && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">What is your vision for the church?</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{wcpData.visionForChurch}</dd>
                    </div>
                  )}
                  {wcpData.howDecisionMade && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">How did you come to this decision?</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{wcpData.howDecisionMade}</dd>
                    </div>
                  )}
                  {wcpData.additionalThoughts && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Additional thoughts</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{wcpData.additionalThoughts}</dd>
                    </div>
                  )}
                </dl>
              ) : (
                <p className="text-sm text-gray-600">Not submitted yet.</p>
              )}
            </CollapsibleSection>

            {/* Sending Church */}
            <CollapsibleSection title="Sending Church">
              {candidate.sendingChurch ? (
                <InfoGrid
                  items={[
                    { label: 'Church name', value: candidate.sendingChurch.churchName },
                    { label: 'Primary contact', value: candidate.sendingChurch.primaryContactName },
                    { label: 'Primary phone', value: candidate.sendingChurch.primaryContactPhone },
                    { label: 'No sending church', value: candidate.sendingChurch.noSendingChurch },
                    { label: 'Explanation', value: candidate.sendingChurch.noSendingChurchExplanation },
                  ]}
                />
              ) : (
                <p className="text-sm text-gray-600">No sending church data submitted.</p>
              )}
            </CollapsibleSection>

            {/* Spouse Interview */}
            {candidate.maritalStatus === 'married' && (
              <CollapsibleSection
                title="Spouse Interview"
                badge={<StatusBadge status={candidate.spouseInterviewSubmitted} trueLabel="Submitted" falseLabel="Pending" />}
              >
                <InfoGrid
                  items={[
                    { label: 'Status', value: candidate.spouseInterviewStatus || 'pending' },
                    { label: 'Submitted', value: candidate.spouseInterviewSubmitted },
                    ...(candidate.spouseInterviewSubmittedAt
                      ? [{ label: 'Submitted at', value: new Date(candidate.spouseInterviewSubmittedAt).toLocaleDateString() }]
                      : []),
                  ]}
                />
              </CollapsibleSection>
            )}

            {/* References */}
            <CollapsibleSection title="References">
              {candidate.references && candidate.references.length > 0 ? (
                <div className="space-y-3">
                  {candidate.references.map((ref: any) => (
                    <div key={ref.id} className="rounded-lg border border-gray-200 px-4 py-3">
                      <div className="text-sm font-semibold text-gray-900">{ref.name}</div>
                      <div className="text-sm text-gray-600">{ref.email}</div>
                      <div className="text-sm text-gray-600">{ref.phone || '—'}</div>
                      <div className="text-xs text-gray-500 mt-2">
                        {ref.relationshipType || 'Relationship'} • {ref.yearsKnown ? `${ref.yearsKnown} years` : 'Years unknown'}
                      </div>
                      {ref.relationshipDescription ? (
                        <div className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{ref.relationshipDescription}</div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-600">No references submitted.</div>
              )}
            </CollapsibleSection>

            {/* Theological Survey */}
            <CollapsibleSection
              title="Theological Survey"
              description="Full submission from the candidate."
              badge={<StatusBadge status={candidate.theologyCompleted} />}
            >
              <SurveyBlock title="Theological Survey" survey={candidate.theologicalSurvey || null} />
            </CollapsibleSection>

            {/* Spiritual Health Surveys */}
            <CollapsibleSection
              title="Spiritual Health Surveys"
              description="Candidate and spouse submissions."
              badge={<StatusBadge status={candidate.spiritualHealthCompleted} />}
            >
              <div className="space-y-4">
                <SurveyBlock
                  title="Candidate Spiritual Health"
                  survey={candidate.spiritualHealthSurveys?.find((s: any) => s?.surveyFor === 'candidate') || null}
                />
                <SurveyBlock
                  title="Spouse Spiritual Health"
                  survey={candidate.spiritualHealthSurveys?.find((s: any) => s?.surveyFor === 'spouse') || null}
                />
              </div>
            </CollapsibleSection>

            {/* Ministry Transitions */}
            <CollapsibleSection
              title="Ministry Transitions"
              badge={<StatusBadge status={candidate.ministryTransitionsCompleted} />}
            >
              {mtData ? (
                <div className="space-y-6">
                  {/* Staff experience flag */}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Has served on church staff?</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {mtData.hasServedOnStaff === true ? 'Yes' : mtData.hasServedOnStaff === false ? 'No' : '—'}
                    </dd>
                  </div>

                  {/* If no staff experience, show ministry background */}
                  {mtData.hasServedOnStaff === false && mtData.ministryBackground && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Ministry background</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{mtData.ministryBackground}</dd>
                    </div>
                  )}

                  {/* Position cards */}
                  {mtPositions.map((pos: any, idx: number) => (
                    <div key={idx} className="rounded-lg border border-gray-200 p-4 space-y-3">
                      <h4 className="text-sm font-semibold text-gray-800">
                        {idx === 0 ? 'Most Recent Staff Position' : 'Previous Staff Position'}
                        {pos.isCurrentPosition && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Current
                          </span>
                        )}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {pos.churchName && (
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Church / Organization</dt>
                            <dd className="text-sm text-gray-900 mt-0.5">{pos.churchName}</dd>
                          </div>
                        )}
                        {pos.role && (
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Role / Title</dt>
                            <dd className="text-sm text-gray-900 mt-0.5">{pos.role}</dd>
                          </div>
                        )}
                        {pos.datesServed && (
                          <div>
                            <dt className="text-xs font-medium text-gray-500">
                              {pos.isCurrentPosition ? 'Dates served (start date)' : 'Dates served'}
                            </dt>
                            <dd className="text-sm text-gray-900 mt-0.5">{pos.datesServed}</dd>
                          </div>
                        )}
                      </div>
                      {!pos.isCurrentPosition && (
                        <div className="space-y-3 pt-2 border-t border-gray-100">
                          {pos.howConcluded && (
                            <div>
                              <dt className="text-xs font-medium text-gray-500">How did this position conclude?</dt>
                              <dd className="text-sm text-gray-900 mt-0.5">
                                {conclusionLabels[pos.howConcluded] || pos.howConcluded}
                              </dd>
                            </div>
                          )}
                          {pos.unresolvedConflict && (
                            <div>
                              <dt className="text-xs font-medium text-gray-500">Any unresolved conflict?</dt>
                              <dd className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap">{pos.unresolvedConflict}</dd>
                            </div>
                          )}
                          {pos.leadershipFavorability && (
                            <div>
                              <dt className="text-xs font-medium text-gray-500">Would leadership speak favorably?</dt>
                              <dd className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap">{pos.leadershipFavorability}</dd>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {mtData.additionalInfo && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Additional information</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{mtData.additionalInfo}</dd>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600">Not submitted yet.</p>
              )}
            </CollapsibleSection>
          </>
        )}
      </div>
    </Layout>
  );
};

export default PortalCandidateProfile;
